/* eslint-disable no-unused-vars */
import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { IconButton, CustomTooltip } from 'components';
import { Pen, Plus, Minus } from 'assets/icons';
import { UpdateBidPopUp } from '../../../updateBidPopUp/UpdateBidPopUp';
import { campaignReportsAPI } from 'services/campaignReports.service';
import { campaignReportsActions } from 'store/actions';
import { showPolipop, useDebounce } from 'utils';
import { isAdminSelector } from 'store/selectors';
import * as S from './RightSideRow.styled';

const BID_STEP = 0.1;
const BID_STEP_CPM = 0.01;

const bidType = {
  1: 'CPM',
  2: 'CPC',
  3: 'CPI',
};

export const Bid = ({ selectedCols, data, cellWidth }) => {
  const isAdmin = useSelector(isAdminSelector);
  const updatedBidsArray = useSelector((state) => state.campaignReports.updatedBidsArray);
  const updatedBid = updatedBidsArray.find((item) => item.id === data.pba_number)?.bid;
  const [updateBidAction, updateBidResult] = campaignReportsAPI.useIncreaseDecreaseBidMutation();

  const { cmp_id } = useParams();
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [bid, setBid] = useState(updatedBid || +data.cm_app_cpi || 0);
  const debouncedValue = useDebounce(bid, 700);
  const { data: appData, isLoading: isAppDataLoading } = campaignReportsAPI.useGetCampaignDataQuery(
    {
      cm_id: cmp_id,
    },
  );
  const isCpm = appData?.bid_type === 1;
  const minBid = isCpm
    ? window.cpm_bid_minimum
    : isAdmin
    ? window.bid_minimum_admin
    : appData?.min_bid;
  // to show actual (publisher-configured) minimum for admin users
  const displayMinBid = isCpm ? minBid : appData?.min_bid ?? minBid;
  const maxBid = isCpm ? window.cpm_bid_maximum : window.bid_maximum;

  const isBidBorder = !(selectedCols.length === 0 && data.eventCountArr?.length > 0);
  const isDisabled = !minBid && minBid?.toString() !== '0';
  const step = isCpm ? BID_STEP_CPM : BID_STEP;

  useEffect(() => {
    setBid(updatedBid || +data.cm_app_cpi || 0);
  }, [data.cm_app_cpi]);

  const handleOpenPopUp = () => {
    setIsOpen(true);
    window.addEventListener('click', closeOnClickOutside, { once: true, capture: true });
    window.addEventListener('scroll', closeOnClickOutside, { once: true });
  };

  const handleClosePopUp = () => {
    setIsOpen(false);
    window.removeEventListener('click', closeOnClickOutside, { once: true, capture: true });
    window.removeEventListener('scroll', closeOnClickOutside, { once: true });
  };

  const closeOnClickOutside = (e) => {
    if (e.type === 'scroll') {
      setIsOpen(false);
      window.removeEventListener('click', closeOnClickOutside, { once: true, capture: true });
      return;
    }

    const isInnerBox = e.target.closest('.MuiTooltip-popper');
    const isButton = e.target.closest('button');

    if (isInnerBox && !isButton) {
      window.addEventListener('click', closeOnClickOutside, { once: true, capture: true });
    } else if (!isInnerBox) {
      e.stopPropagation();
      handleClosePopUp();
    }
  };

  const increaseBid = () => {
    const newBid = +bid + step > maxBid ? bid : (+bid + step).toFixed(2);
    setBid(newBid);
  };

  const decreaseBid = () => {
    const newBid = +bid - step <= minBid ? bid : (+bid - step).toFixed(2);
    setBid(newBid);
  };

  useEffect(() => {
    if (updateBidResult?.isLoading) return;

    if (
      (updatedBid === undefined && debouncedValue != data.cm_app_cpi) ||
      (updatedBid && updatedBid != debouncedValue)
    ) {
      const obj = {
        cpi: debouncedValue?.toString(),
        pbaNumbers: data.pba_number?.toString(),
        cm_id: cmp_id?.toString(),
      };

      updateBidAction(obj);
      dispatch(
        campaignReportsActions.addUpdatedBids([{ bid: debouncedValue, id: data.pba_number }]),
      );
    }
  }, [debouncedValue]);

  useEffect(() => {
    if (updateBidResult && (updateBidResult.isError || updateBidResult.isSuccess)) {
      showPolipop(updateBidResult);
    }
  }, [updateBidResult]);

  return (
    <S.Cell
      style={{
        width: cellWidth.bid,
        borderRight: `${isBidBorder ? '1px solid #e2e8f0' : 'none'}`,
      }}
    >
      <S.Bid>
        <span>{bidType[data.bid_type]}</span> ${+bid}
      </S.Bid>
      <S.ChangeBidBox className='change-bid-box'>
        <CustomTooltip
          text={
            <div className={`change_bid_box_${data.pba_number}`}>
              <UpdateBidPopUp
                onClose={handleClosePopUp}
                pba_number={data.pba_number.toString()}
                location='table'
                defaultBid={+bid}
                onSetBid={setBid}
                minBid={minBid}
                displayMinBid={displayMinBid}
                maxBid={maxBid}
                isCpm={isCpm}
                isAppDataLoading={isAppDataLoading}
              />
            </div>
          }
          content={
            <S.TriggerBox isOpen={isOpen} className='change_bid_trigger_box'>
              <IconButton.Micro
                icon={Pen}
                type={IconButton.btnTypes.tertiary}
                isPressed={isOpen}
                onClick={handleOpenPopUp}
              />
              <S.BidIconButton
                className='change_bid_icon'
                onClick={decreaseBid}
                isDisabled={isDisabled || isAppDataLoading || updateBidResult?.isLoading}
              >
                <Minus />
              </S.BidIconButton>
              <S.BidIconButton
                className='change_bid_icon'
                onClick={increaseBid}
                isDisabled={isDisabled || isAppDataLoading || updateBidResult?.isLoading}
              >
                <Plus />
              </S.BidIconButton>
            </S.TriggerBox>
          }
          isOpen={isOpen}
        />
      </S.ChangeBidBox>
    </S.Cell>
  );
};
